// Portfolio Summary Card Component
// Displays the main portfolio stats (Capital, PnL, Win Rate)
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Wallet, RefreshCw, Filter, DollarSign } from 'lucide-react';

const PortfolioSummaryCard = ({
  language,
  portfolio,
  totalPnl,
  winRate,
  wins,
  losses,
  hasActiveFilters,
  onReset,
  variant = 'paper' // 'paper' or 'real'
}) => {
  if (!portfolio) return null;

  const isPaper = variant === 'paper';
  const gradientClass = isPaper 
    ? 'from-purple-900/30 to-purple-800/10 border-purple-500/30'
    : 'from-green-900/30 to-green-800/10 border-green-500/30';
  const accentColor = isPaper ? 'text-purple-400' : 'text-primary';

  const currentCapital = portfolio.initial_capital + totalPnl;
  const pnlPercent = portfolio.initial_capital > 0 
    ? ((totalPnl / portfolio.initial_capital) * 100).toFixed(2) 
    : 0;

  return (
    <Card className={`bg-gradient-to-r ${gradientClass}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isPaper ? (
              <Wallet className="w-5 h-5 text-purple-400" />
            ) : (
              <DollarSign className="w-5 h-5 text-primary" />
            )}
            {language === 'fr' 
              ? (isPaper ? 'Portfolio Paper' : 'Portfolio Réel')
              : (isPaper ? 'Paper Portfolio' : 'Real Portfolio')}
          </div>
          {isPaper && onReset && (
            <Button 
              onClick={onReset} 
              variant="outline" 
              size="sm"
              className="border-red-500/30 text-red-400 hover:bg-red-500/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filter warning */}
        {hasActiveFilters && (
          <div className="mb-3 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {language === 'fr' ? 'Stats filtrées selon les filtres actifs' : 'Stats filtered by active filters'}
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Initial Capital */}
          <div className="p-3 rounded-lg bg-background/30">
            <div className="text-xs text-muted-foreground">
              {language === 'fr' ? 'Capital Initial' : 'Initial Capital'}
            </div>
            <div className="text-xl font-mono font-bold">
              ${portfolio.initial_capital?.toLocaleString()}
            </div>
          </div>
          
          {/* Current Capital */}
          <div className="p-3 rounded-lg bg-background/30">
            <div className="text-xs text-muted-foreground">
              {language === 'fr' ? 'Capital Actuel' : 'Current Capital'}
            </div>
            <div className={`text-xl font-mono font-bold ${currentCapital >= portfolio.initial_capital ? 'text-primary' : 'text-destructive'}`}>
              ${currentCapital.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
          </div>
          
          {/* PNL Total */}
          <div className="p-3 rounded-lg bg-background/30">
            <div className="text-xs text-muted-foreground">PNL Total</div>
            <div className={`text-xl font-mono font-bold ${totalPnl >= 0 ? 'text-primary' : 'text-destructive'}`}>
              {totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}$ 
              <span className="text-sm ml-1">({pnlPercent >= 0 ? '+' : ''}{pnlPercent}%)</span>
            </div>
          </div>
          
          {/* Win Rate */}
          <div className="p-3 rounded-lg bg-background/30">
            <div className="text-xs text-muted-foreground">Win Rate</div>
            <div className={`text-xl font-mono font-bold ${accentColor}`}>{winRate}%</div>
            <div className="text-xs text-muted-foreground">
              {wins}W / {losses}L
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioSummaryCard;
